import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { ChatMessage } from '../../models/chat.models';
import { ChatInputPayload } from '../chat-input/chat-input.component';
import { ChatTurnController } from '../../services/chat-turn.controller';
import { ChatService } from '../../services/chat.service';

@Component({
  selector: 'app-chat-drawer',
  templateUrl: './chat-drawer.component.html',
  styleUrls: ['./chat-drawer.component.scss'],
  providers: [ChatTurnController],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatDrawerComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  activeId: string | null = null;
  messages: ReadonlyArray<ChatMessage> = [];

  /** Controls the inline delete-confirmation banner in the header. */
  showDeleteConfirm = false;

  constructor(
    private readonly chatService: ChatService,
    private readonly turnController: ChatTurnController,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  get busy(): boolean { return this.turnController.busy; }

  ngOnInit(): void {
    combineLatest([this.chatService.conversations$, this.chatService.activeConversationId$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([list, activeId]: [{ id: string }[], string | null]) => {
        this.activeId = activeId;
        if (activeId) {
          const convo = this.chatService.getConversation(activeId);
          this.messages = convo?.messages ?? [];
        } else {
          this.messages = [];
        }
        if (!activeId && list.length > 0) {
          this.chatService.setActive(list[0].id);
        } else if (!activeId && list.length === 0) {
          const title = this.translate.instant('chatAi.sidebar.untitled') as string;
          this.chatService.createConversation(title || 'Quick chat');
        }
        // Dismiss confirm banner if the conversation changed underneath
        this.showDeleteConfirm = false;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.turnController.cancel();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSend(payload: ChatInputPayload | string): void {
    if (!this.activeId) return;
    if (typeof payload === 'string') {
      this.turnController.sendTurn(this.activeId, payload);
    } else {
      this.turnController.sendTurn(this.activeId, payload.text, payload.attachments);
    }
  }

  onSuggestionPick(prompt: string): void { this.onSend(prompt); }

  onNavigate(route: string): void {
    this.router.navigate([route]);
  }

  onNewChat(): void {
    this.showDeleteConfirm = false;
    this.turnController.cancel();
    const title = this.translate.instant('chatAi.sidebar.untitled') as string;
    this.chatService.createConversation(title || 'Quick chat');
  }

  /** First click on X: show inline confirmation banner. */
  onDeleteChat(): void {
    if (!this.activeId) return;
    this.showDeleteConfirm = true;
    this.cdr.markForCheck();
  }

  onConfirmDelete(): void {
    if (!this.activeId) return;
    this.showDeleteConfirm = false;
    this.turnController.cancel();
    this.chatService.deleteConversation(this.activeId);
  }

  onCancelDelete(): void {
    this.showDeleteConfirm = false;
    this.cdr.markForCheck();
  }

  onCancel(): void {
    this.turnController.cancel();
  }
}
